import React, { memo, useCallback } from 'react';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { getMemberId, getMemberNickname } from '../TeamChat.utils';
import TeamChatMemberAvatar from '../TeamChatMemberAvatar';

import {
  MENTION_SUGGESTIONS_AVATAR_SIZE,
  MENTION_SUGGESTIONS_EMPTY_LABEL,
  MENTION_SUGGESTIONS_KEYBOARD_HINTS,
} from './TeamChatMentionSuggestions.constants';
import {
  emptyStateStyles,
  focusedItemStyles,
  footerStyles,
  hintKeyStyles,
  hintStyles,
  itemStyles,
  listStyles,
  nameStyles,
  rootStyles,
} from './TeamChatMentionSuggestions.styles';
import { getHighlightedNameSegments } from './TeamChatMentionSuggestions.utils';

const TeamChatMentionSuggestions = memo(
  ({ members, focusedMemberId = null, query = '', onSelectMember, isHidden = false }) => {
    const onItemMouseDown = useCallback(
      member => e => {
        e.preventDefault();
        onSelectMember(member);
      },
      [onSelectMember],
    );

    if (isHidden) {
      return null;
    }

    const hasMembers = !!members.length;
    const trimmedQuery = query.trim();
    const emptyLabel = trimmedQuery
      ? `${MENTION_SUGGESTIONS_EMPTY_LABEL} "@${trimmedQuery}"`
      : `${MENTION_SUGGESTIONS_EMPTY_LABEL}.`;

    return (
      <Box sx={rootStyles}>
        {hasMembers && (
          <Box sx={listStyles}>
            {members.map(member => {
              const memberId = getMemberId(member);
              const isFocused = memberId === focusedMemberId;
              const nickname = getMemberNickname(member);
              const segments = getHighlightedNameSegments(nickname, query);

              return (
                <Box
                  key={memberId}
                  sx={isFocused ? focusedItemStyles : itemStyles}
                  onMouseDown={onItemMouseDown(member)}
                >
                  <TeamChatMemberAvatar
                    member={member}
                    showPresence={false}
                    size={MENTION_SUGGESTIONS_AVATAR_SIZE}
                  />
                  <Box sx={nameStyles}>
                    {segments.map(segment => (
                      <Typography
                        component="span"
                        key={segment.key}
                        variant={segment.isMatch ? 'bold14' : 'regular14'}
                      >
                        {segment.text}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
        {!hasMembers && (
          <Typography sx={emptyStateStyles} variant="regular14">
            {emptyLabel}
          </Typography>
        )}
        <Box sx={footerStyles}>
          {MENTION_SUGGESTIONS_KEYBOARD_HINTS.map(hint => (
            <Box key={hint.label} sx={hintStyles}>
              <Box component="span" sx={hintKeyStyles}>
                {hint.key}
              </Box>
              <Typography variant="regular12">{hint.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  },
);
TeamChatMentionSuggestions.propTypes = {
  members: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectMember: PropTypes.func.isRequired,
  focusedMemberId: PropTypes.string,
  query: PropTypes.string,
  isHidden: PropTypes.bool,
};

export default TeamChatMentionSuggestions;
